// Browser-local library of imported games (plan Item B2), modelled on
// `botHistory.ts`. Studies live in localStorage — mirroring how computer games are
// kept — so the Study section survives reloads with no server round-trip and no DB
// migration (decision #1). Best-effort: storage may be unavailable (private mode)
// or full, in which case writes degrade to no-ops.

import type { PgnMeta } from "./pgn.ts";

export interface StudyGame {
  id: string;
  savedAt: number;
  moves: { uci: string }[];
  initialFen?: string;
  meta: PgnMeta;
  source: "import" | "clipboard" | "file";
}

const KEY = "chess.studies.v1";
/** Cap on how many studies the browser keeps (newest wins). Exported so the
 *  import UI can report how many of a large paste couldn't be stored. */
export const MAX_STUDIES = 200;

export interface StudySaveResult {
  /** IDs allocated for the input games, whether or not the write succeeded. */
  ids: string[];
  /** IDs confirmed present after the write, in input order. */
  persistedIds: string[];
  error?: "storage";
}

/** All saved studies, newest first. Returns `[]` on any storage/parse failure. */
export function listStudies(): StudyGame[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as StudyGame[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function getStudy(id: string): StudyGame | undefined {
  return listStudies().find((g) => g.id === id);
}

/** Persist one study (newest first, capped) and return its confirmed id. */
export function saveStudy(g: Omit<StudyGame, "id" | "savedAt">): string | undefined {
  return saveStudies([g]).persistedIds[0];
}

/** Persist a batch of studies in a *single* read-modify-write (newest first,
 *  capped at [`MAX_STUDIES`]). Returns allocated and confirmed ids separately.
 *
 *  Batching is the point: doing one read-modify-write per game (the old loop of
 *  `saveStudy`) re-applied the cap on every call, so importing more than the cap
 *  in one paste evicted the earliest games of that same import. Here the whole
 *  batch is prepended and the cap applied once, so a large import keeps its most
 *  recent `MAX_STUDIES` games and callers can see how many didn't fit. */
export function saveStudies(games: Omit<StudyGame, "id" | "savedAt">[]): StudySaveResult {
  const now = Date.now();
  const created: StudyGame[] = games.map((g, i) => ({ ...g, id: shortId(), savedAt: now + i }));
  const ids = created.map((g) => g.id);
  try {
    // Reverse so the LAST game passed ends up newest (front), matching the old
    // one-at-a-time prepend behaviour; then cap the combined list once.
    const all = [...created].reverse().concat(listStudies()).slice(0, MAX_STUDIES);
    localStorage.setItem(KEY, JSON.stringify(all));
    const present = new Set(listStudies().map((game) => game.id));
    const persistedIds = ids.filter((id) => present.has(id));
    if (persistedIds.length !== Math.min(created.length, MAX_STUDIES)) {
      return { ids, persistedIds, error: "storage" };
    }
    return { ids, persistedIds };
  } catch {
    return { ids, persistedIds: [], error: "storage" };
  }
}

/** Remove one study by id. Best-effort. */
export function deleteStudy(id: string): void {
  try {
    const all = listStudies().filter((g) => g.id !== id);
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

/** Wipe the whole study library. Best-effort. */
export function clearStudies(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** A short, collision-resistant-enough id (8 chars). Prefers crypto.randomUUID. */
function shortId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID().slice(0, 8);
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`.slice(0, 8);
}
